import { inject } from '@angular/core';
import { CanMatchFn } from '@angular/router';

import { combineLatest } from 'rxjs';
import { map, skipWhile, switchMap, take } from 'rxjs/operators';

import { AuthService } from 'src/app/modules/core/services/auth.service';
import { PostCloseDateService } from 'src/app/modules/core/services/post-close-date.service';
import { SettingsService } from 'src/app/modules/core/services/settings.service';

import { YourHomeFacade } from 'src/app/modules/ngrx-store/your-home/your-home.facade';

export const realtorAccessGuard: CanMatchFn = () => {
	const yourHomeFacade = inject(YourHomeFacade);
	const settingsService = inject(SettingsService);
	const postCloseDateService = inject(PostCloseDateService);
	const authService = inject(AuthService);

	return authService.canActivate().pipe(
		skipWhile(canActivate => !canActivate), // Wait until authenticated
		take(1),
		switchMap(() => yourHomeFacade.homeDetails$),
		skipWhile(homeDetails => !homeDetails), // Wait until homeDetails is defined
		switchMap(() => combineLatest([
			yourHomeFacade.realtorInfo$,
			yourHomeFacade.homeDetails$,
			yourHomeFacade.realtorEnabled$,
			settingsService.getSettings()
		])),
		take(1),
		map(([realtorInfo, homeDetails, realtorEnabled]) => {
			if (realtorEnabled) {
				// If realtor access is enabled, check for realtor info and whether it's been 31 days since COE
				const closeOfEscrow = homeDetails?.lot.closeOfEscrow;
				const isThirtyOneDaysPostClose = closeOfEscrow ? postCloseDateService.getIsThirtyOneDaysSince(closeOfEscrow) : false;

				// Allow access if realtor info exists and it's not 31 days post close, otherwise redirect to the default error page
				return !!realtorInfo && !isThirtyOneDaysPostClose;
			}
			return false;
		})
	);
};
